import {Component, EventEmitter, Output} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {SharedOfferSetRuleService} from '../../../../../service/shared-offer-set-rule.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {OfferSetSharingRuleFilterDefinitionModel} from '../../../../../model/offer-set-sharing-rule-filter-definition.model';
import {DynamicDialogRef, DynamicDialogConfig, DialogService} from 'primeng/dynamicdialog';
import {OfferSetSharingRuleModel} from '../../../../../model/offer-set-sharing-rule.model';
import {OfferSetSharingRuleRequestModel} from '../../../../../model/offer-set-sharing-rule-request.model';
import {OfferSetSharingRuleFilterModel} from '../../../../../model/offer-set-sharing-rule-filter.model';
import {EventEmitterService} from '../../../../../service/event-emitter.service';
import {SharingRuleFilterModel} from '../../../../../model/sharing-rule-filter.model';
import {MakeModel} from '../../../../../model/make.model';
import {SharedOfferSetRulesPreviewComponent} from './shared-offer-set-rules-preview/shared-offer-set-rules-preview.component';

@Component({
  selector: 'auto-shared-offer-set-rules-entry',
  templateUrl: './shared-offer-set-rules-entry.component.html',
  styleUrls: ['./shared-offer-set-rules-entry.component.scss']
})
export class SharedOfferSetRulesEntryComponent {
  ruleForm: FormGroup;
  accountFilters: any[] = [];
  offerFilters: any[] = [];
  globalFilters = [
    {
      label: 'Make',
      value: 1,
      type: 'string'
    }
  ];
  ruleData?: OfferSetSharingRuleModel;
  filterDefinitionMap = [];
  ruleProcessing = false;
  sharingRuleFilterRequest: SharingRuleFilterModel;
  offerSetSharingRuleRequest: OfferSetSharingRuleRequestModel;
  appliedFilters: any;
  filterDefinitions: OfferSetSharingRuleFilterDefinitionModel[] = [];
  makes: MakeModel[] = [];
  @Output() refreshData: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private formBuilder: FormBuilder,
    private offerSetRuleService: SharedOfferSetRuleService,
    private messageService: MessageService,
    private dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private eventEmitterService: EventEmitterService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
  ) {
    this.ruleForm = this.formBuilder.group({
      ruleName: [''],
      sourceAccount: [''],
      offerSetFilter: this.formBuilder.array([]),
      accountFilter: this.formBuilder.array([]),
      globalFilter: this.formBuilder.array([])
    });
    this.makes = this.config.data.makes;
    this.ruleData = this.config.data.rule;
    this.accountFilters = this.config.data.accountFilters;
    this.offerFilters = this.config.data.offerFilters;
    this.filterDefinitionMap = this.config.data.filterDefinitionMap;
    this.filterDefinitions = this.config.data.filterDefinitions;
  }

   ngOnInit() {
    if (this.ruleData) {
      this.populateForm(this.ruleData);
    }
  }

  /**
   * Populate the form with the data from the rule
   */
  populateForm(rule: OfferSetSharingRuleModel) {
    this.ruleForm.patchValue({
      ruleName: rule.ruleName,
      sourceAccount: rule.sourceAccount['id']
    });

    (this.ruleForm.get('offerSetFilter') as FormArray).clear();
    (this.ruleForm.get('accountFilter') as FormArray).clear();
    (this.ruleForm.get('globalFilter') as FormArray).clear();

    rule.offerSetSharingRuleFilters.forEach(filter => {
      let filterOptions = this.filterDefinitionMap[filter.offerSetSharingRuleFilterDefinitionId];
      if (filterOptions === 'Make' || filterOptions === 'Account ID') {
        this.handleMultiSelectPopulation(filter);
      }

      const filterGroup = this.formBuilder.group({
        propertyValue: [filter.offerSetSharingRuleFilterDefinitionId],
        selectedOperator: [filter.operator],
        value: [filter.value]
      });
      if (this.filterDefinitionMap[filter.offerSetSharingRuleFilterDefinitionId] !== 'Make') {
        if (filter.filterType === 'account') {
          (this.ruleForm.get('accountFilter') as FormArray).push(filterGroup);
        } else if (filter.filterType === 'offerSet') {
          (this.ruleForm.get('offerSetFilter') as FormArray).push(filterGroup);
        }
      } else {
        if (filter.filterType === 'offerSet') {
          (this.ruleForm.get('globalFilter') as FormArray).push(filterGroup);
        }
      }
    });
  }

  /**
   * Converts the incoming filter data to data for use with the multi-select options
   */
  handleMultiSelectPopulation(filter: OfferSetSharingRuleFilterModel): OfferSetSharingRuleFilterModel {
    if (!filter.value || filter.value === '') {
      filter.value = [] as any;
    }

    if (filter.value.includes(',')) {
      filter.value = filter.value.split(',').map((value) => {
        let result: any = value;
        if (!isNaN(Number(value))) {
          result = Number(value);
        }
        return result;
      }) as any;
    }

    if (!Array.isArray(filter.value) && !filter.value.includes(',')) {
      filter.value = [Number(filter.value)] as any;
    }

    return filter;
  }

  /**
   * Method to open Preview Modal
   * @param appliedFilters
   */
  openPreviewFiltersModal(appliedFilters: any) {
    const ref = this.dialogService.open(SharedOfferSetRulesPreviewComponent, {
      header: 'Preview ' + this.ruleForm.get('ruleName').value + ' Rule',
      modal: true,
      width: '40vw',
      closable: true,
      data: {
        appliedFilters: appliedFilters,
        isSave: true
      }
    });
    ref.onClose.subscribe((returnedAction) => {
      if (returnedAction === 'save') {
        this.saveRuleRequest(this.offerSetSharingRuleRequest);
      } else if (returnedAction === 'cancel') {
        this.ruleProcessing = false;
      }
    });
  }

  /**
   * Method to preview the filters
   */
  previewFilters() {
    this.offerSetSharingRuleRequest = this.buildOfferSetSharingRuleRequest();
    this.sharingRuleFilterRequest = this.buildSharingRuleFilterRequest(this.offerSetSharingRuleRequest);
    this.offerSetRuleService.getSharedOfferSetRuleFilter(this.sharingRuleFilterRequest).subscribe({
        next: (appliedFilters) => {
          this.appliedFilters = appliedFilters;
          this.openPreviewFiltersModal(appliedFilters);
        },
        error: (error) => {
          let message = 'Failed to get preview for filters.';
          if (error.violations && error.violations.length > 0) {
            message = error.violations[0].message;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: message
          });
        }
      }
    );
  }

  /**
   * Save the rule to the database
   *
   * @param offerSetSharingRuleRequest
   * @private
   */
  saveRuleRequest(offerSetSharingRuleRequest: OfferSetSharingRuleRequestModel) {
    this.ruleProcessing = true;
    this.offerSetRuleService.updateSharedOfferSetRule(offerSetSharingRuleRequest).subscribe({
        next: (offerSetSharingRule) => {
          this.offerSetRuleService.applySharedOfferSetRuleFilters(offerSetSharingRule.id).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Rule saved successfully.'
                });
                this.dialogRef.close();
                this.ruleForm.markAsPristine();
                this.eventEmitterService.emitEvent('loadSharedOfferSetRules');
              },
              error: () => {
                this.ruleProcessing = false;
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Failed to apply filters.'
                });
              }
            }
          );
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save rule.'
          });
          this.ruleProcessing = false;
        }
      }
    );
  }

  /**
   * Build the request object for the API call
   */
  buildOfferSetSharingRuleRequest(): OfferSetSharingRuleRequestModel {
    let offerSetSharingRuleRequest = new OfferSetSharingRuleRequestModel();
    if (this.ruleData) {
      offerSetSharingRuleRequest.offerSetSharingRuleId = this.ruleData.id;
    }
    offerSetSharingRuleRequest.ruleName = this.ruleForm.get('ruleName').value;
    offerSetSharingRuleRequest.sourceAccountId = this.ruleForm.get('sourceAccount').value;
    offerSetSharingRuleRequest.offerSetSharingRuleFilters = this.getSharingRulesFiltersForRequest();

    return offerSetSharingRuleRequest;
  }

  /**
   * Get the filters from the form and convert them to the format required for the request
   */
  private getSharingRulesFiltersForRequest(): any[] {
    let filters = [];
    this.ruleForm.get('offerSetFilter').value.forEach(filter => {
      let offerSetSharingRuleFilter = this.buildOfferSetSharingRuleFilter(filter, 'offerSet');
      filters.push(offerSetSharingRuleFilter);
    });
    this.ruleForm.get('accountFilter').value.forEach(filter => {
      let offerSetSharingRuleFilter = this.buildOfferSetSharingRuleFilter(filter, 'account');
      filters.push(offerSetSharingRuleFilter);
    });
    this.ruleForm.get('globalFilter').value.forEach((filter) => {
      this.filterDefinitions.forEach(
        (filterDefinition) => {
          if (filterDefinition.filterName === this.filterDefinitionMap[filter.propertyValue]) {
            filter.propertyValue = filterDefinition.id;
            let offerSetSharingRuleFilter = this.buildOfferSetSharingRuleFilter(filter, filterDefinition.filterType);
            filters.push(offerSetSharingRuleFilter);
          }
        }
      );
    });

    return filters;
  }

  /**
   * Build the filter object for the request
   */
  buildOfferSetSharingRuleFilter(filter: any, filterType: string): OfferSetSharingRuleFilterModel {
    let offerSetSharingRuleFilter = new OfferSetSharingRuleFilterModel();
    let value = filter.value;

    if (Array.isArray(value)) {
      value = value.join(',');
    }

    offerSetSharingRuleFilter.offerSetSharingRuleFilterDefinitionId = filter.propertyValue;
    offerSetSharingRuleFilter.value = value;
    offerSetSharingRuleFilter.operator = filter.selectedOperator;
    offerSetSharingRuleFilter.filterType = filterType;

    return offerSetSharingRuleFilter;
  }

  /**
   * Build the sharing rule filter request
   */
  buildSharingRuleFilterRequest(offerSetSharingRuleRequest: OfferSetSharingRuleRequestModel): SharingRuleFilterModel {
    let sharingRuleFilterRequest = new SharingRuleFilterModel();
    sharingRuleFilterRequest.sourceAccountId = offerSetSharingRuleRequest.sourceAccountId;
    offerSetSharingRuleRequest.offerSetSharingRuleFilters.forEach((filter) => {
      sharingRuleFilterRequest.offerSetSharingRuleFilters.push(
        {
          filterName: this.filterDefinitionMap[filter.offerSetSharingRuleFilterDefinitionId],
          filterType: filter.filterType,
          operator: filter.operator,
          value: filter.value
        }
      );
    });

    return sharingRuleFilterRequest;
  }

  /**
   * Method to check if the confirm button should be disabled
   */
  checkDisabledConfirmButton() {
    return this.isInvalidForm() || this.hasDuplicateRuleName() || this.ruleProcessing || this.hasEmptyFilterFields();
  }

  /**
   * Check if any filter has empty fields or if no filters are added
   */
  hasEmptyFilterFields(): boolean {
    const allFilters = this.getAllFilters();

    let hasEmptyFields = false;

    if (allFilters.length === 0) {
      hasEmptyFields = true;
    } else {
      for (const filterGroup of allFilters) {
        if (this.isFilterEmpty(filterGroup)) {
          hasEmptyFields = true;
          break;
        }
      }
    }

    return hasEmptyFields;
  }

  /**
   * Gather all filters from the form
   */
  private getAllFilters(): FormGroup[] {
    const globalFilterArray = this.ruleForm.get('globalFilter') as FormArray;
    const offerSetFilterArray = this.ruleForm.get('offerSetFilter') as FormArray;
    const accountFilterArray = this.ruleForm.get('accountFilter') as FormArray;

    return [
      ...(globalFilterArray?.controls ?? []),
      ...(offerSetFilterArray?.controls ?? []),
      ...(accountFilterArray?.controls ?? [])
    ] as FormGroup[];
  }

  /**
   * Determine if a filter group is empty
   */
  private isFilterEmpty(filterGroup: FormGroup): boolean {
    const propertyValue = filterGroup.get('propertyValue')?.value;
    const selectedOperator = filterGroup.get('selectedOperator')?.value;
    const filterValue = filterGroup.get('value')?.value;

    const isEmptyArray =
      Array.isArray(filterValue) && filterValue.length === 0;

    return (
      !propertyValue ||
      !selectedOperator ||
      !filterValue ||
      isEmptyArray
    );
  }

  /**
   * Close the modal
   */
  closeModal() {
    if (!this.ruleForm.dirty) {
      this.dialogRef.close();
    } else {
      this.confirmationService.confirm({
        message: 'If you close now, your changes will be lost. Do you want to discard them or keep editing?',
        header: 'You Have Unsaved Changes',
        acceptLabel: 'Discard Changes',
        rejectLabel: 'Cancel (Keep Editing)',
        icon: 'pi pi-info-circle',
        accept: () => {
          this.dialogRef.close();
        },
        reject: () => {}
      });
    }
  }

  /**
   * Check if the form is invalid
   */
  isInvalidForm() {
    return this.ruleForm.get('ruleName').value === '' || this.ruleForm.get('sourceAccount').value === '';
  }

  /**
   * Check if the entered Rule name already exists
   */
  hasDuplicateRuleName(): boolean {
    const ruleName = this.ruleForm.get('ruleName').value;
    const isAdding = this.config.data.openedModal === 'add';

    const duplicate = this.config.data.sharedOfferSetRules.find((rule) =>
      rule.ruleName === ruleName &&
      (isAdding || rule.id !== this.ruleData.id)
    );

    return duplicate !== undefined;
  }
}
